import { css } from '@emotion/css';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { BestFriendProfileErrorType } from '../../../features/bestFriends';
import { replaceAllSpaces } from '../../../lib/utils';
import { FriendItem } from '../../../models/FriendItem';
import { FriendProfile } from '../../../models/FriendProfile';
import { NormalButton } from '../../shared/buttons/NormalButton';
import { CharacterImage } from '../../shared/CharacterImage';
import { reveal } from '../../shared/keyframe-animations';
import { ProfileErrorText } from './ProfileErrorText';

const detailPagePath = '/players/best-friends/detail';

interface ReportMenuProps {
  friendItem: FriendItem;
  profile?: FriendProfile;
  profileErrorType?: BestFriendProfileErrorType;
}

export const ReportMenu: React.FC<ReportMenuProps> = ({
  friendItem,
  profile,
  profileErrorType
}) => {
  const { t } = useTranslation(['best_friends', 'translation']);
  const history = useHistory();
  const friendId = friendItem.id;

  const onBlockClick = useCallback(() => {
    history.replace(`${detailPagePath}/${friendId}/menu/2`);
  }, [history, friendId]);

  const onReportClick = useCallback(() => {
    history.replace(`/players/best-friends/report/${friendId}`);
  }, [friendId, history]);

  const onCancelClick = useCallback(() => {
    history.replace(`${detailPagePath}/${friendId}`);
  }, [history, friendId]);

  const { image, name, isReported } = friendItem;

  return (
    <div
      className={css`
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        color: #4d3906;
        text-align: center;
        height: 100%;
        .friend,
        .button {
          animation: 0.2s cubic-bezier(0.3, 0.82, 0.54, 1.23) ${reveal} both;
        }
        .friend {
          opacity: 0;
          transform: scale(0.9);
          animation-delay: 0.1s;
        }
        .image {
          width: 88px;
          height: 88px;
          margin: 0 auto;
        }
        .name {
          padding: 8px 0;
        }
        .button {
          width: 100%;
          opacity: 0;
          visibility: hidden;
          transform: scale(0.9);
          margin: 8px 0 0;

          &:nth-child(1) {
            animation-delay: 0.3s;
          }
          &:nth-child(2) {
            animation-delay: 0.4s;
          }
          &:nth-child(3) {
            animation-delay: 0.5s;
            margin-bottom: 4px;
          }

          @media (min-width: 370px) {
            margin: 12px 0 0;
          }
        }
      `}
    >
      <div className="friend">
        <div className="image">
          <CharacterImage
            src={image}
            isPlayer={true}
            isReported={isReported}
            imageAriaLabel={t('aria_player_image')}
          />
        </div>
        <p className="name">
          {isReported ? t('translation:reported_player') : replaceAllSpaces(name)}
        </p>
      </div>

      <div className="buttons">
        <div className="button">
          <NormalButton onClick={onBlockClick}>{t('button_delete_best_friends')}</NormalButton>
        </div>
        <div className="button">
          <NormalButton onClick={onReportClick} disabled={!profile}>
            {t('button_report')}
          </NormalButton>
        </div>
        <div className="button">
          <NormalButton onClick={onCancelClick}>
            {t('translation:dialog_button_cancel')}
          </NormalButton>
        </div>
        <ProfileErrorText profileErrorType={profileErrorType} />
      </div>
    </div>
  );
};
